import shortNumber from 'short-number';

function getPrerenderedData() {
  if (ON_SERVER) {
    return {};
  }

  const element = document.querySelector(`script[type="__PREACT_CLI_DATA__"]`);
  if (!element) {
    return {};
  }

  try {
    return JSON.parse(decodeURI(element.textContent)).preRenderData;
  } catch {
    return {};
  }
}

export const CWV_SUMMARY = {
  NOT_FOUND: 'not_found',
  NOT_QUERIED: 'not_queried',
  UNKNOWN: 'unknown',
  PASS: 'pass',
  FAIL: 'fail',
};

export const FORM_FACTOR = {
  PHONE: 'PHONE',
  DESKTOP: 'DESKTOP',
  ALL: 'ALL',
};

export const FORM_FACTOR_RPM = {
  phone: 'mobile',
  desktop: 'desktop',
  tablet: 'tablet',
  all: 'all',
};

function isOnRpm() {
  return process.env.RELEASE_ENVIRONMENT === 'rpm';
}

export const ON_SERVER = typeof window === 'undefined';

export const ON_RPM = isOnRpm();

export const ON_RUNNER = !ON_RPM;

export const PRERENDERED_DATA = getPrerenderedData();

export const PUBLIC_FOLDER = ON_RPM ? '/pharos/dashboard' : '/r/rpm';

export const GOOD_COLOR = 'rgb(11 128 67)';
export const BAD_COLOR = 'rgb(197 57 41)';
export const AVG_COLOR = 'rgb(240 147 0)';

export const MAX_API_LIMIT = 288;

export const DISPLAY_FORMAT = 'LLL, dd';
export const DISPLAY_FORMAT_FULL = 'DD';

export const BAR_THIN_EXTRA_OPTIONS = {};

export const BAR_THINK_EXTRA_OPTIONS = {
  barPercentage: 1.0,
  categoryPercentage: 1.0,
};

export const LINE_THIN_EXTRA_OPTIONS = {};

export const LINE_THINK_EXTRA_OPTIONS = {};

function percentTickFormatter(value) {
  return `${value}%`;
}

function shortNumberFormatter(value) {
  return shortNumber(value);
}

//#region Histogram

const CWV_P75_HISTOGRAM_SCALES = {
  interaction: {
    intersect: false,
    mode: 'index',
  },
  scales: {
    x: {
      grid: {
        display: false,
        color: '#eee',
        borderColor: 'grey',
        tickColor: 'grey',
      },
      title: {
        color: '#757575',
        display: false,
        text: 'DATE',
      },
      ticks: {
        color: '#757575',
        maxRotation: 0,
        minRotation: 0,
        autoSkip: true,
      },
    },
    y: {
      suggestedMax: 75,
      suggestedMin: 75,
      stepSize: 25,
      beingAtZero: true,
      grid: {
        color: '#eee',
        borderColor: '#999',
        tickColor: '#aaa',
      },
      title: {
        color: '#757575',
        display: false,
        text: '% of users',
      },
      ticks: {
        color: '#757575',
        stepSize: 25,
        callback: percentTickFormatter,
      },
    },
  },
};

function percentLabelFormatter(context) {
  const {
    dataset: { label },
    formattedValue,
  } = context;
  return `${label}: ${formattedValue}%`;
}

const HISTOGRAM_PLUGINS = {
  legend: {
    display: false,
    position: 'top',
  },
  tooltip: {
    callbacks: {
      label: percentLabelFormatter,
    },
  },
  annotation: {
    annotations: {
      goodThreshold: {
        display: true,
        type: 'line',
        yMin: 75,
        yMax: 75,
        borderColor: '#000',
        borderWidth: 2,
      },
    },
  },
};

export const HISTOGRAM_INESTABILITY_CHART_OPTIONS = {
  responsive: true,
  scales: {
    x: {
      stacked: true,
      grid: {
        display: false,
        color: '#eee',
        borderColor: 'grey',
        tickColor: 'grey',
      },
      title: {
        color: '#757575',
        display: false,
        text: 'DATE',
      },
      ticks: {
        color: '#757575',
        maxRotation: 0,
        minRotation: 0,
        autoSkip: true,
      },
    },
    y: {
      stacked: true,
      min: 0,
      max: 100,
      grid: {
        color: '#eee',
        borderColor: '#999',
        tickColor: '#aaa',
      },
      title: {
        color: '#757575',
        display: false,
        text: '% of users',
      },
      ticks: {
        color: '#757575',
      },
    },
  },
  plugins: {
    title: {
      display: true,
      text: 'Axis inestability',
    },
    legend: {
      display: true,
      position: 'top',
    },
    tooltip: {
      callbacks: {
        label: percentLabelFormatter,
      },
    },
  },
};

export const HISTOGRAM_LCP_CHART_OPTIONS = {
  responsive: true,
  ...CWV_P75_HISTOGRAM_SCALES,
  plugins: {
    title: {
      display: true,
      text: 'LCP evolution',
    },
    ...HISTOGRAM_PLUGINS,
  },
};

export const HISTOGRAM_CLS_CHART_OPTIONS = {
  responsive: true,
  ...CWV_P75_HISTOGRAM_SCALES,
  plugins: {
    title: {
      display: true,
      text: 'CLS evolution',
    },
    ...HISTOGRAM_PLUGINS,
  },
};

export const HISTOGRAM_FID_CHART_OPTIONS = {
  responsive: true,
  ...CWV_P75_HISTOGRAM_SCALES,
  plugins: {
    title: {
      display: true,
      text: 'FID evolution',
    },
    ...HISTOGRAM_PLUGINS,
  },
};

export const HISTOGRAM_INP_CHART_OPTIONS = {
  responsive: true,
  ...CWV_P75_HISTOGRAM_SCALES,
  plugins: {
    title: {
      display: true,
      text: 'INP evolution',
    },
    ...HISTOGRAM_PLUGINS,
  },
};

export const HISTOGRAM_TTFB_CHART_OPTIONS = {
  responsive: true,
  ...CWV_P75_HISTOGRAM_SCALES,
  plugins: {
    title: {
      display: true,
      text: 'TTFB evolution',
    },
    ...HISTOGRAM_PLUGINS,
  },
};

export const HISTOGRAM_FCP_CHART_OPTIONS = {
  responsive: true,
  ...CWV_P75_HISTOGRAM_SCALES,
  plugins: {
    title: {
      display: true,
      text: 'FCP evolution',
    },
    ...HISTOGRAM_PLUGINS,
  },
};

//#endregion

//#region P75

export const GOOD_COLOR_TRANSPARENT = 'rgba(12, 206, 106, 0.3)';
export const BAD_COLOR_TRANSPARENT = 'rgba(255, 78, 67, 0.3)';
export const AVG_COLOR_TRANSPARENT = 'rgba(255, 164, 0, 0.3)';

function makeP75Scales(threshold) {
  return {
    interaction: {
      intersect: false,
    },
    scales: {
      x: {
        grid: {
          display: false,
          color: '#eee',
          borderColor: 'grey',
          tickColor: 'grey',
        },
        title: {
          color: '#757575',
          display: false,
          text: 'DATE',
        },
        ticks: {
          color: '#757575',
          maxRotation: 0,
          minRotation: 0,
          autoSkip: true,
        },
      },
      y: {
        suggestedMax: threshold,
        suggestedMin: threshold,
        grid: {
          color: '#ccc',
          borderColor: '#999',
          tickColor: '#aaa',
        },
        title: {
          color: '#757575',
          display: false,
          text: 'P75 value',
        },
        ticks: {
          color: '#757575',
        },
      },
    },
  };
}

function getP75Annotations(goodUpper) {
  return {
    annotation: {
      annotations: {
        good: {
          display: true,
          type: 'line',
          borderColor: '#000',
          borderWidth: 2,
          yMin: goodUpper,
          yMax: goodUpper,
        },
      },
    },
  };
}

export const OVERVIEW_CHART_OPTIONS = {
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      callbacks: {
        label: function (context) {
          const {
            formattedValue,
            datasetIndex,
            raw,
            dataset: { label },
          } = context;
          const value =
            datasetIndex === 0
              ? formattedValue
              : raw
              ? `${raw.toFixed(1)}%`
              : '0';
          return `${label}      ${value}`;
        },
      },
    },
  },
  interaction: {
    mode: 'index',
    intersect: false,
  },
  scales: {
    x: {
      grid: {
        display: false,
        color: '#eee',
        borderColor: 'grey',
        tickColor: 'grey',
      },
      title: {
        display: false,
      },
      ticks: {
        color: '#757575',
        autoSkip: true,
        maxRotation: 0,
        minRotation: 0,
      },
    },
    y: {
      suggestedMin: 75,
      suggestedMax: 75,
      type: 'linear',
      position: 'left',
      grid: {
        color: '#eee',
        borderColor: '#999',
        tickColor: '#aaa',
      },
      ticks: {
        stepSize: 25,
        color: '#757575',
        callback: percentTickFormatter,
      },
    },
    y2: {
      min: 0,
      suggestedMax: 100,
      type: 'linear',
      position: 'right',
      ticks: {
        color: '#757575',
        callback: shortNumberFormatter,
      },
      grid: {
        display: false,
      },
    },
  },
};

export const CRUX_OVERVIEW_CHART_OPTIONS = {
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      callbacks: {
        label: function (context) {
          const {
            raw,
            dataset: { label },
          } = context;
          const value = raw ? `${raw.toFixed(1)}%` : '0';
          return `${label}      ${value}`;
        },
      },
    },
  },
  interaction: {
    mode: 'index',
    intersect: false,
  },
  scales: {
    x: {
      grid: {
        display: false,
        color: '#eee',
        borderColor: 'grey',
        tickColor: 'grey',
      },
      title: {
        display: false,
      },
      ticks: {
        color: '#757575',
        autoSkip: true,
        maxRotation: 0,
        minRotation: 0,
      },
    },
    y: {
      suggestedMin: 75,
      suggestedMax: 75,
      type: 'linear',
      position: 'left',
      grid: {
        color: '#eee',
        borderColor: '#999',
        tickColor: '#aaa',
      },
      ticks: {
        stepSize: 25,
        color: '#757575',
        callback: percentTickFormatter,
      },
    },
  },
};

const P75_PLUGINS = {
  legend: {
    display: false,
    position: 'top',
  },
};

export const GENERIC_GOOD_UX_CHART = {
  responsive: true,
  interaction: {
    mode: 'index',
    intersect: false,
  },
  scales: {
    x: {
      grid: {
        display: false,
        color: '#eee',
        borderColor: 'grey',
        tickColor: 'grey',
      },
      title: {
        color: '#757575',
        display: false,
        text: 'DATE',
      },
      ticks: {
        color: '#757575',
        maxRotation: 0,
        minRotation: 0,
        autoSkip: true,
      },
    },
    y: {
      min: 0,
      max: 100,
      grid: {
        color: '#eee',
        borderColor: '#999',
        tickColor: '#aaa',
      },
      title: {
        color: '#757575',
        display: false,
        text: 'P75 value',
      },
      ticks: {
        stepSize: 25,
        color: '#757575',
        callback: percentTickFormatter,
      },
    },
  },
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
    },
    title: {
      display: true,
      text: 'CWV good ux %',
    },
    tooltip: {
      callbacks: {
        label: percentLabelFormatter,
      },
    },
  },
};

export const P75_LCP_CHART_OPTIONS = {
  responsive: true,
  ...makeP75Scales(2500),
  plugins: {
    title: {
      display: true,
      text: 'LCP evolution',
    },
    ...P75_PLUGINS,
    ...getP75Annotations(2500, 4000),
  },
};

export const P75_CLS_CHART_OPTIONS = {
  responsive: true,
  ...makeP75Scales(0.1),
  plugins: {
    title: {
      display: true,
      text: 'CLS evolution',
    },
    ...P75_PLUGINS,
    ...getP75Annotations(0.1, 0.25),
  },
};

export const P75_FID_CHART_OPTIONS = {
  responsive: true,
  ...makeP75Scales(100),
  plugins: {
    title: {
      display: true,
      text: 'FID evolution',
    },
    ...P75_PLUGINS,
    ...getP75Annotations(100, 300),
  },
};

export const P75_INP_CHART_OPTIONS = {
  responsive: true,
  ...makeP75Scales(200),
  plugins: {
    title: {
      display: true,
      text: 'INP evolution',
    },
    ...P75_PLUGINS,
    ...getP75Annotations(200, 500),
  },
};

export const P75_TTFB_CHART_OPTIONS = {
  responsive: true,
  ...makeP75Scales(800),
  plugins: {
    title: {
      display: true,
      text: 'TTFB evolution',
    },
    ...P75_PLUGINS,
    ...getP75Annotations(800, 1800),
  },
};

export const P75_FCP_CHART_OPTIONS = {
  responsive: true,
  ...makeP75Scales(1800),
  plugins: {
    title: {
      display: true,
      text: 'FCP evolution',
    },
    ...P75_PLUGINS,
    ...getP75Annotations(1800),
  },
};

//#endregion

//#region LCP related charts

export const VARIATION_COUNTER_PIE_CHART = {
  responsive: true,
  plugins: {
    legend: {
      position: 'top',
    },
  },
};

export const PERCENTILE_LINE_CHART = {
  responsive: true,
  interaction: {
    intersect: false,
  },
  scales: {
    x: {
      display: true,
      title: {
        display: true,
        text: 'Percentile',
      },
    },
    y: {
      display: true,
      title: {
        display: true,
        text: 'Milliseconds',
      },
      suggestedMin: 0,
    },
  },
};

export const QUERY_VARIATION_LINE_CHART = {
  indexAxis: 'y',
  responsive: true,
};

//#endregion

export const RM_DRAWER_MENU_ITEMS = [
  { caption: 'Dashboard', href: '/r/dashboard/posts' },
  { caption: 'Add Post', href: '/core/community/add_post' },
  { caption: 'Stats', href: '/core/dashboard/stats#overview' },
  { caption: 'Discovery', href: '/core/discovery' },
  { caption: 'Design Tool', href: '/r/layouts-editor/' },
  { caption: 'Bookmarklet', href: '/core/dashboard/bookmarklet' },
];

export const NEUTRAL_COLORS = ['#937DC2', '#1597BB', '#6E85B7'];

export const ISSUE_COUNT_TIMELINE_CHART_OPTIONS = {
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      callbacks: {
        label: function (context) {
          const {
            formattedValue,
            datasetIndex,
            raw,
            dataset: { label },
          } = context;
          const value =
            datasetIndex === 0 ? formattedValue : raw ? `${raw}` : '0';
          return `${label}      ${value}`;
        },
      },
    },
  },
  interaction: {
    mode: 'index',
    intersect: false,
  },
  scales: {
    x: {
      grid: {
        display: false,
        color: '#eee',
        borderColor: 'grey',
        tickColor: 'grey',
      },
      title: {
        display: false,
      },
      ticks: {
        color: '#757575',
        autoSkip: true,
        maxRotation: 0,
        minRotation: 0,
      },
    },
    y: {
      type: 'linear',
      position: 'left',
      grid: {
        color: '#eee',
        borderColor: '#999',
        tickColor: '#aaa',
      },
      ticks: {
        color: '#757575',
      },
    },
  },
};
